import { AppShell, Avatar, Code, Group } from '@mantine/core'
import {
  IconChartBar,
  IconChecklist,
  IconClipboardList,
  IconDatabasePlus,
  IconDeviceDesktop,
  IconFilterSearch,
  IconHistory,
  IconLogout,
  IconPackageOff,
  IconScissors,
  IconShoppingCart,
  IconZoom,
} from '@tabler/icons-react'

import { modals } from '@mantine/modals'
import { Link } from '@tanstack/react-router'
import Logo from 'common/Logo'
import { useAccount, useLogout } from 'hooks/auth'
import { useMemo } from 'react'
import { RoleName } from 'types/auth'
import classes from './Navbar.module.scss'

type NavbarItem = {
  label: string
  icon: typeof IconZoom
  requiredRoles?: Array<RoleName>
} & (
  | {
      link: string
    }
  | {
      onClick: () => void
    }
)

export function Navbar() {
  const { data: account } = useAccount()
  const logout = useLogout()

  const data: Array<NavbarItem> = useMemo(
    () => [
      { link: '/', label: 'Znajdź produkt', icon: IconZoom },
      { link: '/add', label: 'Dodaj produkt', icon: IconDatabasePlus },
      {
        onClick: () =>
          modals.openContextModal({
            modal: 'addWork',
            centered: true,
            title: 'Dodaj wykonaną pracę',
            innerProps: {},
          }),
        label: 'Dodaj wykonaną pracę',
        icon: IconScissors,
      },
      {
        onClick: () =>
          modals.openContextModal({
            modal: 'addShortage',
            centered: true,
            title: 'Dodaj brak produktu',
            innerProps: {},
          }),
        label: 'Dodaj brak',
        icon: IconPackageOff,
      },
      {
        label: 'Zarządzaj brakami',
        icon: IconChecklist,
        link: '/shortages',
        requiredRoles: [RoleName.ADMIN],
      },
      // { link: '/place', label: 'Zajrzyj na półkę', icon: IconArchive },
      { link: '/advanced-search', label: 'Zaawansowane wyszukiwanie', icon: IconFilterSearch },
      {
        onClick: () =>
          modals.openContextModal({
            modal: 'checkOrderHistory',
            centered: true,
            title: 'Sprawdź status zamówienia',
            innerProps: {},
            size: 'xxl',
          }),
        label: 'Status zamówienia',
        icon: IconHistory,
      },
      { link: '/orders', label: 'Zamówienia', icon: IconClipboardList },
      { link: '/analytics', label: 'Statystyki', icon: IconChartBar, requiredRoles: [RoleName.ADMIN] },
      { link: '/admin', label: 'Panel administracyjny', icon: IconDeviceDesktop, requiredRoles: [RoleName.ADMIN] },
      // { link: '/settings', label: 'Ustawienia', icon: IconSettings, requiredRoles: [RoleName.ADMIN] },
    ],
    []
  )

  const links = data.map((item) => {
    if (item.requiredRoles && !item.requiredRoles.some((role) => account.roles.some((r) => r.name === role))) {
      return null
    }
    if ('link' in item) {
      return (
        <Link
          className={classes.link}
          activeProps={
            item.link
              ? {
                  'data-active': true,
                }
              : undefined
          }
          to={item.link ?? ''}
          search={{}}
          key={item.label}
          target={item.link?.startsWith('http') ? '_blank' : undefined}
        >
          <item.icon className={classes.linkIcon} stroke={1.5} />
          <span>{item.label}</span>
        </Link>
      )
    } else {
      return (
        <div className={classes.link} key={item.label} onClick={item.onClick}>
          <item.icon className={classes.linkIcon} stroke={1.5} />
          <span>{item.label}</span>
        </div>
      )
    }
  })

  return (
    <AppShell.Navbar className={classes.navbar}>
      <div className={classes.navbarMain}>
        <Group className={classes.header} justify="space-between">
          <Logo size="md" p={0} />
          <Code fw={700}>v1.2.0</Code>
        </Group>
        {links}
      </div>
      <div>
        <Link
          className={classes.link}
          activeProps={{ 'data-active': true }}
          to={'https://abcfirany.pl'}
          target="_blank"
        >
          <IconShoppingCart className={classes.linkIcon} stroke={1.5} />
          <span>Przejdź do sklepu</span>
        </Link>
      </div>
      <div className={classes.footer}>
        <Link to="/profile" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Group mb="sm">
            <Avatar color="initials" name={`${account.firstName} ${account.lastName}`} />
            <div>
              {account.firstName} {account.lastName}
            </div>
          </Group>
        </Link>

        <a href="#" className={classes.link} onClick={() => logout.mutate()}>
          <IconLogout className={classes.linkIcon} stroke={1.5} />
          <span>Wyloguj</span>
        </a>
      </div>
    </AppShell.Navbar>
  )
}
